import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Menu, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSelectSite } from '../../api/hooks/useSelectSite'
import { useAppSelector, useCurrentSite } from '../../store/store.hooks'
import { Site } from '../../types/site.types'
import { useUserSitesByOrg } from '../../user/hooks/user.hooks'
import { logger } from '../../utils/logger'

const PRODUCT_ROUTES = [
  { licenseName: 'WeSaw', link: '/WeSaw' },
  { licenseName: 'WePlane', link: '/WePlane' },
  { licenseName: 'WeTrack', link: '/WeTrack' },
  { licenseName: 'WeSchedule', link: '/WeSchedule' },
  { licenseName: 'WeDry', link: '/WeDry' },
]

interface BreadcrumbBarProps {
  currentPageLabel?: string
  onSiteClick?: () => void
}

const BreadcrumbBar = ({ currentPageLabel, onSiteClick }: BreadcrumbBarProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const parentOrganization = useAppSelector((state) => state.auth.organization)
  const productLicenses = useAppSelector((state) => state.auth.productLicenses)
  const sites = useUserSitesByOrg()
  const currentSite = useCurrentSite()
  const [orgAnchorEl, setOrgAnchorEl] = useState<null | HTMLElement>(null)
  const [siteAnchorEl, setSiteAnchorEl] = useState<null | HTMLElement>(null)
  const [productAnchorEl, setProductAnchorEl] = useState<null | HTMLElement>(null)

  const licenseNames = productLicenses?.map((l) => l.licenseName) ?? []
  const availableProducts = PRODUCT_ROUTES.filter((p) => licenseNames.includes(p.licenseName))
  const currentProduct = PRODUCT_ROUTES.find((p) => location.pathname.startsWith(p.link))

  const { selectSite } = useSelectSite()

  const currentOrg = sites.find((org) => org.sites.some((s) => s.id === currentSite?.id))
  const currentOrgSites = currentOrg?.sites ?? []
  const isMultiSite = currentOrgSites.length > 1
  const isAtHub = !currentSite

  const handleSiteSelect = async (site: Site) => {
    logger.log('[BreadcrumbBar] Current site:', currentSite)
    await selectSite(site)
    navigate('/')
    setOrgAnchorEl(null)
    setSiteAnchorEl(null)
    onSiteClick?.()
  }

  const separator = <Typography sx={{ opacity: 0.4, mx: 0.5 }}>/</Typography>

  const handleClick = (event: React.MouseEvent<Element>) => {
    setOrgAnchorEl(event.currentTarget as HTMLElement)
  }

  return (
    <Box
    id="breadcrumb"
      sx={{
        display: 'flex',
        alignItems: 'center',
        ml: { xs: 1, md: 2 },
        mr: { xs: 1, md: 2 },
        flexWrap: 'nowrap',
      }}
    >
      {!isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              opacity: isAtHub ? 1 : 0.7,
              fontWeight: isAtHub ? 500 : 400,
              fontSize: '1rem',
              cursor: isAtHub ? 'default' : 'pointer',
              '&:hover': !isAtHub ? { opacity: 1 } : {},
            }}
            onClick={!isAtHub ? () => navigate('/hub') : undefined}
          >
            {currentOrg?.organizationName ?? parentOrganization?.name}
          </Typography>
        </Box>
      )}

      {currentSite && (
        <>
          {!isMobile && separator}
          <Box
          id="breadcrumb-site"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={isMobile && isMultiSite ? (e) => setSiteAnchorEl(e.currentTarget) : undefined}
          >
            <Typography
              sx={{
                opacity: currentPageLabel ? 0.7 : 1,
                fontWeight: currentPageLabel ? 400 : 500,
                cursor: 'pointer',
                fontSize: '1rem',
                whiteSpace: 'nowrap',
                maxWidth: isMobile ? '120px' : 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              onClick={!isMobile ? () => { navigate('/'); onSiteClick?.() } : undefined}
            >
              {currentSite.name}
            </Typography>
            {isMultiSite && !isAtHub && (
              <ExpandMoreIcon
                sx={{ fontSize: '1rem', ml: 0.25, opacity: 0.7, cursor: 'pointer' }}
                onClick={(e) => setOrgAnchorEl(e.currentTarget as unknown as HTMLElement)}
              />
            )}
            {isMobile && isMultiSite && (
              <ExpandMoreIcon sx={{ fontSize: '1rem', ml: 0.25, opacity: 0.7, cursor: 'pointer' }} />
            )}
          </Box>
          <Menu anchorEl={orgAnchorEl} open={Boolean(orgAnchorEl)} onClose={() => setOrgAnchorEl(null)}>
            {currentOrgSites.map((site) => (
              <MenuItem key={site.id} selected={site.id === currentSite?.id} onClick={() => handleSiteSelect(site)}>
                {site.name}
              </MenuItem>
            ))}
          </Menu>

          {isMobile && (
            <Menu anchorEl={siteAnchorEl} open={Boolean(siteAnchorEl)} onClose={() => setSiteAnchorEl(null)}>
              {currentOrgSites.map((site) => (
                <MenuItem key={site.id} selected={site.id === currentSite?.id} onClick={() => handleSiteSelect(site)}>
                  {site.name}
                </MenuItem>
              ))}
            </Menu>
          )}
        </>
      )}

      {currentPageLabel && currentSite && (
        <>
          {separator}
          <Typography sx={{ opacity: 1, fontWeight: 500, fontSize: '1rem', whiteSpace: 'nowrap' }}>
            {currentPageLabel}
          </Typography>
        </>
      )}

      {currentProduct && currentSite && (
        <>
          {separator}
          <Box 
          id="breadcrumb-product"
          sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              sx={{ fontWeight: 500, fontSize: '1rem', whiteSpace: 'nowrap', cursor: availableProducts.length > 1 ? 'pointer' : 'default' }}
              onClick={availableProducts.length > 1 ? (e) => setProductAnchorEl(e.currentTarget) : undefined}
            >
              {currentProduct.licenseName}
            </Typography>
            {availableProducts.length > 1 && (
              <ExpandMoreIcon
                sx={{ fontSize: '1rem', ml: 0.25, cursor: 'pointer' }}
               onClick={(e) => setProductAnchorEl(e.currentTarget as unknown as HTMLElement)}
              />
            )}
          </Box>
          <Menu anchorEl={productAnchorEl} open={Boolean(productAnchorEl)} onClose={() => setProductAnchorEl(null)}>
            {availableProducts.map((product) => (
              <MenuItem
                key={product.licenseName}
                selected={product.link === currentProduct.link}
                onClick={() => { navigate(product.link); setProductAnchorEl(null) }}
              >
                {product.licenseName}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  )
}

export default BreadcrumbBar
